/** Every status in sort order, with its badge and meaning. `sales` is the sales label in the org's naming. */
export const PERFORMANCE_STATUSES = [
  {
    status: "Not published",
    badge: "neutral",
    meaning: (_sales: string) => "No published demand to compare against.",
  },
  {
    status: "Over",
    badge: "blue",
    meaning: (sales: string) => `${sales} 10% or more above expected demand.`,
  },
  {
    status: "On track",
    badge: "green",
    meaning: (sales: string) => `${sales} within 10% of expected demand.`,
  },
  {
    status: "Under",
    badge: "yellow",
    meaning: (sales: string) => `${sales} 10% or more below expected demand.`,
  },
  {
    status: "No sales",
    badge: "neutral",
    meaning: (sales: string) => `No ${sales.toLowerCase()} in plan weeks.`,
  },
] as const

export type PerformanceStatus = (typeof PERFORMANCE_STATUSES)[number]["status"]

export const performanceStatusDef = (status: PerformanceStatus) =>
  PERFORMANCE_STATUSES.find((d) => d.status === status)!

const rank = (s: PerformanceStatus | null) =>
  s == null ? PERFORMANCE_STATUSES.length : PERFORMANCE_STATUSES.findIndex((d) => d.status === s)

/** Sorts in `PERFORMANCE_STATUSES` order, N/A last. */
export const comparePerformanceStatus = (
  a: PerformanceStatus | null,
  b: PerformanceStatus | null,
): number => rank(a) - rank(b)

/**
 * Status from the primary period's expected demand vs sales in the same weeks. Null is N/A: published
 * demand the selectors can't split (`expectedNA` once something is published). Expected and sales only
 * count weeks with both, so 0/0 means no sales in plan weeks. A published 0 is a plan, so any sale
 * against it is Over.
 */
export const performanceStatus = ({
  anyPublished,
  expectedNA,
  expected,
  sales,
}: {
  anyPublished: boolean
  expectedNA: boolean
  expected: number
  sales: number
}): PerformanceStatus | null => {
  if (!anyPublished) return "Not published"
  if (expectedNA) return null
  if (expected === 0) return sales > 0 ? "Over" : "No sales"
  if (Math.abs(((sales - expected) / expected) * 100) < 10) return "On track"
  return sales > expected ? "Over" : "Under"
}
